import type { Doc } from "building-types/doc";
import { parseInstanceOptionSetId } from "building-types/options/instanceOptionSetId";
import { archetypeOnlyOptions } from "building-types/options/optionDefinitions";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import type { DocOptionSet } from "building-types/options/optionSet";
import type { OptionSetOverrides } from "building-types/options/optionTemplateTypes";
import objectIsEmpty from "common/utils/objectIsEmpty";
import type { Mutable } from "common/utils/types";
import { docGetActiveOptionTemplateOverrides } from "../docGetOptionTemplates";
import { docToOptionDefinitions } from "./docGetInstanceOptionSets";
import { buildInstanceOptionSetIdsByArchetype } from "./instanceOptionSetIndex";
import { optionSetDefaultOptionId } from "./optionSetBaseUtils";
import { mergeOptionsOverridesIntoOptions } from "./optionTemplate";

/**
 * This is one of the most important functions in the entire application, responsible
 * for merging a list of Option Templates into a table of Option Sets. This is the heart
 * of the cascading functionality that allows for our plan hierarchy to operate.
 *
 * Though this function can be used with an arbitrary set of Option Templates, typically,
 * it will be used to figure out what Option Sets a Home Plan can work with, from top (lowest precedence) to
 * bottom (highest precedence):
 *
 * 1. Studio Document (doc.optionSets) provides our "universe" - the original Option Set definitions
 * 2. Studio Document (non-disabled list of DocOptionTemplate converted to an OptionTemplate)
 * 3. Master Plan (OptionTemplate)
 * 4. Location Plan (OptionTemplate)
 * 5. Lot (OptionTemplate)
 * 6. Home Plan (OptionTemplate)
 *
 * Studio Documents are currently the ONLY entity in this chain that support multiple Option Templates. This
 * merging of non-disabled Option Templates back into the Doc happens in docOptionSetOverridesNormalize.
 *
 * @param {OptionDefinitions} definitions - The source definitions, containing all valid Option Set definitions
 * @param {OptionSetOverrides[]} optionTemplates - An ordered list of Option Templates, where the last entry has highest priority
 *
 * @returns {OptionDefinitions} The new table of Option definitions, with all overrides from Option Templates applied
 */

export function mergeOptionTemplatesIntoDefinitions(
  definitions: OptionDefinitions,
  optionTemplates: (OptionSetOverrides | null | undefined)[],
): OptionDefinitions {
  // 1. Create a copy of our original Option Sets
  const baseOptionSets: Mutable<Doc["optionSets"]> = { ...definitions.optionSets };
  const baseInstanceOptionSets: Mutable<Doc["instanceOptionSets"]> = { ...definitions.instanceOptionSets };
  const instanceOptionSetIdsByArchetype = buildInstanceOptionSetIdsByArchetype(baseInstanceOptionSets);

  // 2. Iterate over the list of Option Templates
  for (const optionTemplate of optionTemplates) {
    // 2a. Skip any empty Option Templates
    if (!optionTemplate || objectIsEmpty(optionTemplate)) {
      continue;
    }

    // First pass: archetype overrides (propagates disabled to instances)
    for (const optionSetId in optionTemplate) {
      const optionSetOverride = optionTemplate[optionSetId];

      if (!optionSetOverride || objectIsEmpty(optionSetOverride)) {
        continue;
      }

      // Skip compound IDs (instance overrides) - handled in second pass
      if (parseInstanceOptionSetId(optionSetId)) {
        continue;
      }

      // Skip any overrides for Option Sets not found in our base table
      if (!baseOptionSets[optionSetId]) {
        continue;
      }

      const baseOptionSet = baseOptionSets[optionSetId];

      // Merge overrides for any Options within the Option Set
      const newOptions: DocOptionSet["options"] = mergeOptionsOverridesIntoOptions(
        baseOptionSet.options,
        optionSetOverride.options,
      );

      const newDefaultOption =
        optionSetOverride.defaultValue ?? optionSetDefaultOptionId(baseOptionSet, baseOptionSets);

      /**
       * Apply our overrides to the base set of Option Sets
       *
       * NOTE: defaultValue is coerced from a boolean value to string if needed,
       * as the removal of BooleanParam did not migrate existing PlanConfig objects.
       */
      const defaultValue = typeof newDefaultOption === "boolean" ? String(newDefaultOption) : newDefaultOption;

      baseOptionSets[optionSetId] = {
        ...baseOptionSet,
        ...optionSetOverride,
        options: newOptions,
        defaultValue: defaultValue ?? "",
      };

      if (optionSetOverride.disabled !== undefined) {
        const instanceIds = instanceOptionSetIdsByArchetype[optionSetId];
        if (instanceIds) {
          for (const instanceId of instanceIds) {
            const instance = baseInstanceOptionSets[instanceId];
            baseInstanceOptionSets[instanceId] = {
              ...instance,
              disabled: optionSetOverride.disabled,
            };
          }
        }
      }
    }

    // Second pass: instance overrides via compound IDs (takes precedence)
    for (const optionSetId in optionTemplate) {
      const optionSetOverride = optionTemplate[optionSetId];

      if (!optionSetOverride || objectIsEmpty(optionSetOverride)) {
        continue;
      }

      const parsedInstanceId = parseInstanceOptionSetId(optionSetId);
      if (!parsedInstanceId) {
        continue;
      }

      const instance = baseInstanceOptionSets[optionSetId];
      if (!instance) {
        continue;
      }

      baseInstanceOptionSets[optionSetId] = {
        ...instance,
        ...(optionSetOverride.disabled !== undefined ? { disabled: optionSetOverride.disabled } : {}),
      };
    }
  }

  return {
    optionSets: baseOptionSets,
    instanceOptionSets: baseInstanceOptionSets,
  };
}

/**
 * @deprecated Use mergeOptionTemplatesIntoDefinitions instead.
 * When instance-level template overrides are needed, mergeOptionTemplatesIntoDefinitions takes OptionDefinitions
 * with instanceOptionSets. This function is used when callers only need merged archetype optionSets and do not
 * change instanceOptionSets.
 */
export default function mergeOptionTemplatesIntoOptionSets(
  optionSets: Doc["optionSets"],
  optionTemplates: (OptionSetOverrides | null | undefined)[],
): Doc["optionSets"] {
  const definitions = archetypeOnlyOptions(optionSets);
  const result = mergeOptionTemplatesIntoDefinitions(definitions, optionTemplates);
  return result.optionSets;
}

export function mergeDocOptionTemplatesIntoOptionSets(doc: Doc): OptionDefinitions {
  return mergeOptionTemplatesIntoDefinitions(docToOptionDefinitions(doc), [docGetActiveOptionTemplateOverrides(doc)]);
}

export function getDocWithOverrides(doc: Doc): Doc {
  const definitions = mergeDocOptionTemplatesIntoOptionSets(doc);
  return { ...doc, ...definitions };
}
